import { ClipboardList, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

const slides = [
  {
    image: "/images/nanny.png",
    alt: "Professional Nanny and Baby Sitting",
    title: "Nanny & Baby Sitting"
  },
  {
    image: "/images/patient_care.png",
    alt: "Professional Patient Care",
    title: "Patient Care"
  },
  {
    image: "/images/home_nursing.png",
    alt: "Compassionate Elderly Care",
    title: "Elderly Care"
  }
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[90vh] md:min-h-screen flex items-center bg-gradient-to-br from-[#dd6b8e]/20 via-white to-[#99F6E4]/40 overflow-hidden pt-12 md:pt-20"
    >
      <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#14B8A6]/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E0F2FE]/50 blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-left"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#dd6b8e]/10 text-[#dd6b8e] text-sm font-semibold font-body mb-6">
              Trusted Home Healthcare in India
            </span>
            <h1 className="font-rotis text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] tracking-tight leading-[1.1] mb-6">
              Compassionate Care at Home for Your{" "}
              <span className="text-[#dd6b8e]">Loved Ones</span>
            </h1>
            <p className="text-lg leading-relaxed text-[#64748B] font-body max-w-lg mb-10">
              Trusted, verified caregivers,nurses and nannies at your doorstep.
              Professional home healthcare that your family deserves.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                data-testid="hero-callback-btn"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full font-semibold bg-[#14B8A6] text-white hover:bg-[#0D9488] transition-all duration-300 transform hover:-translate-y-1 active:scale-95 shadow-lg shadow-teal-500/20 font-body"
              >
                <ClipboardList className="w-5 h-5" />
                Request Call Back
              </a>
            </div>

            <div className="flex gap-8 mt-12">
              <div>
                <p className="font-heading text-2xl font-bold text-[#0F172A]">24/7</p>
                <p className="text-sm text-[#64748B] font-body">Support Available</p>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <p className="font-heading text-2xl font-bold text-[#0F172A]">Verified</p>
                <p className="text-sm text-[#64748B] font-body">Caregivers</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="blob-shape overflow-hidden w-full max-w-[500px] aspect-square mx-auto shadow-2xl relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    src={slides[currentSlide].image}
                    alt={slides[currentSlide].alt}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </AnimatePresence>

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? "w-6 bg-[#14B8A6]" : "bg-white/50"
                        }`}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#14B8A6]/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#14B8A6]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748B] font-body">Quick Response</p>
                    <p className="text-sm font-semibold text-[#0F172A] font-body">Within 2 Hours</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
